import { useState, useEffect } from 'react'
import {
  X,
  User,
  MapPin,
  Calendar,
  Phone,
  AlertTriangle,
  Bell,
  Check,
  Loader2,
  ShieldCheck,
  ShieldAlert,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { getAvatarColor, getInitials } from '@/lib/utils/avatarUtils'
import { toE164 } from '@/lib/utils/phoneUtils'
import { useUpdateProfile } from '@/hooks'
import { subscriptionRepo } from '@/lib/repositories'
import { requireSupabaseClient } from '@/lib/supabase'
import type { ShippingAddress } from '@/lib/repositories/interfaces/IProfileRepository'
import { useTranslation } from 'react-i18next'

interface EditProfileModalProps {
  open: boolean
  onClose: () => void
  currentUsername: string
  currentDisplayName?: string
  currentBio?: string
  currentPhoneNumber?: string
  currentBirthdate?: string
  currentShippingAddress?: ShippingAddress
  currentEmail: string
  usernameUpdatedAt?: Date
  onSave: (newUsername?: string) => void
}

export function EditProfileModal({
  open,
  onClose,
  currentUsername,
  currentDisplayName = '',
  currentBio = '',
  currentPhoneNumber = '',
  currentBirthdate = '',
  currentShippingAddress,
  currentEmail,
  usernameUpdatedAt,
  onSave,
}: EditProfileModalProps) {
  const { t } = useTranslation()
  const [username, setUsername] = useState(currentUsername)
  const [displayName, setDisplayName] = useState(currentDisplayName)
  const [bio, setBio] = useState(currentBio)
  const [phoneNumber, setPhoneNumber] = useState(currentPhoneNumber)
  const [birthdate, setBirthdate] = useState(currentBirthdate)
  const [street, setStreet] = useState(currentShippingAddress?.street || '')
  const [city, setCity] = useState(currentShippingAddress?.city || '')
  const [state, setState] = useState(currentShippingAddress?.state || '')
  const [zip, setZip] = useState(currentShippingAddress?.zip || '')
  const [country, setCountry] = useState(
    currentShippingAddress?.country || 'USA'
  )
  const updateProfileMutation = useUpdateProfile()

  // Phone verification state
  const [showOtpVerify, setShowOtpVerify] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [pendingPhone, setPendingPhone] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [phoneVerified, setPhoneVerified] = useState(!!currentPhoneNumber)

  // SMS opt-in state
  const [smsOptedIn, setSmsOptedIn] = useState(false)
  const [smsLoading, setSmsLoading] = useState(false)

  const canChangeUsername = (() => {
    if (!usernameUpdatedAt) return true
    const hoursSinceChange =
      (Date.now() - usernameUpdatedAt.getTime()) / (1000 * 60 * 60)
    return hoursSinceChange >= 24
  })()

  const hoursUntilUsernameChange = (() => {
    if (!usernameUpdatedAt || canChangeUsername) return 0
    const hoursSinceChange =
      (Date.now() - usernameUpdatedAt.getTime()) / (1000 * 60 * 60)
    return Math.ceil(24 - hoursSinceChange)
  })()

  // Reset form only when modal opens (not on prop changes while open)
  useEffect(() => {
    if (!open) return

    // Don't reset if OTP verification is in progress
    if (showOtpVerify) return

    setUsername(currentUsername)
    setDisplayName(currentDisplayName)
    setBio(currentBio)
    setPhoneNumber(currentPhoneNumber)
    setBirthdate(currentBirthdate)
    setStreet(currentShippingAddress?.street || '')
    setCity(currentShippingAddress?.city || '')
    setState(currentShippingAddress?.state || '')
    setZip(currentShippingAddress?.zip || '')
    setCountry(currentShippingAddress?.country || 'USA')
    setShowOtpVerify(false)
    setOtpCode('')
    setPendingPhone('')
    setPhoneVerified(!!currentPhoneNumber)

    subscriptionRepo
      .getSMSSubscription()
      .then((sub) => setSmsOptedIn(!!sub?.isActive))
      .catch(() => setSmsOptedIn(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleSave = async () => {
    const trimmedUsername = username.trim().toLowerCase()
    if (trimmedUsername !== currentUsername) {
      if (trimmedUsername.length < 3) {
        toast.error(t('usernameTooShort'), {
          description: t('usernameTooShortDesc'),
        })
        return
      }
      if (trimmedUsername.length > 20) {
        toast.error(t('usernameTooLong'), {
          description: t('usernameTooLongDesc'),
        })
        return
      }
      if (!/^[a-z0-9_]+$/.test(trimmedUsername)) {
        toast.error(t('invalidUsernameTitle'), {
          description: t('invalidUsernameDesc'),
        })
        return
      }
    }

    if (displayName.trim().length > 50) {
      toast.error(t('displayNameTooLong'), {
        description: t('displayNameTooLongDesc'),
      })
      return
    }

    if (bio.trim().length > 160) {
      toast.error(t('bioTooLong'), {
        description: t('bioTooLongDesc'),
      })
      return
    }

    const hasShippingAddress = street || city || state || zip
    const shippingAddress: ShippingAddress | null = hasShippingAddress
      ? {
          street: street.trim() || undefined,
          city: city.trim() || undefined,
          state: state.trim() || undefined,
          zip: zip.trim() || undefined,
          country: country.trim() || undefined,
        }
      : null

    try {
      const isUsernameChanged = trimmedUsername !== currentUsername
      const formattedPhone =
        phoneNumber.replace(/\D/g, '').length >= 10
          ? toE164(phoneNumber)
          : undefined

      const isPhoneChanged =
        formattedPhone && formattedPhone !== currentPhoneNumber

      await updateProfileMutation.mutateAsync({
        username: currentUsername,
        data: {
          username: isUsernameChanged ? trimmedUsername : undefined,
          displayName: displayName.trim() || trimmedUsername,
          bio: bio.trim(),
          phoneNumber: formattedPhone || undefined,
          birthdate: birthdate || null,
          shippingAddress,
        },
      })

      toast.success(t('profileUpdated'), {
        description: isUsernameChanged
          ? t('usernameChangedDesc')
          : t('changesSaved'),
      })

      onSave(isUsernameChanged ? trimmedUsername : undefined)

      // If phone changed, show OTP verification inline instead of closing
      if (isPhoneChanged) {
        setPendingPhone(formattedPhone)
        setShowOtpVerify(true)
        setPhoneVerified(false)
        toast.success(t('verifyPhoneCodeSent'), {
          description: t('verifyPhoneCodeSentDesc'),
        })
      } else {
        onClose()
      }
    } catch (error) {
      console.error('Failed to update profile:', error)
      const errorMessage =
        error instanceof Error ? error.message : t('pleaseTryAgain')
      toast.error(t('failedToUpdateProfile'), {
        description: errorMessage,
      })
    }
  }

  const handleVerifyOtp = async () => {
    if (otpCode.length < 6 || !pendingPhone) return

    try {
      setIsVerifying(true)
      const supabase = requireSupabaseClient()

      const { error } = await supabase.auth.verifyOtp({
        phone: pendingPhone,
        token: otpCode,
        type: 'phone_change',
      })

      if (error) throw new Error(error.message)

      setPhoneVerified(true)
      setShowOtpVerify(false)
      setOtpCode('')
      toast.success(t('phoneVerified'), {
        description: t('phoneVerifiedDesc'),
      })
    } catch (error) {
      console.error('Phone verification failed:', error)
      toast.error(t('verifyFailed'), {
        description:
          error instanceof Error ? error.message : t('pleaseTryAgain'),
      })
    } finally {
      setIsVerifying(false)
    }
  }

  const handleSmsToggle = async () => {
    const currentPhone = currentPhoneNumber || toE164(phoneNumber)
    if (!currentPhone || currentPhone.replace(/\D/g, '').length < 10) {
      toast.error(t('addPhoneFirst'))
      return
    }

    try {
      setSmsLoading(true)
      if (smsOptedIn) {
        await subscriptionRepo.unsubscribeFromSMS()
        setSmsOptedIn(false)
        toast.success(t('smsDisabled'))
      } else {
        await subscriptionRepo.subscribeToSMS(toE164(currentPhone))
        setSmsOptedIn(true)
        toast.success(t('smsEnabled'), {
          description: t('smsEnabledDesc'),
        })
      }
    } catch (error) {
      console.error('SMS toggle failed:', error)
      toast.error(t('pleaseTryAgain'))
    } finally {
      setSmsLoading(false)
    }
  }

  const handleCancel = () => {
    setUsername(currentUsername)
    setDisplayName(currentDisplayName)
    setBio(currentBio)
    setPhoneNumber(currentPhoneNumber)
    setBirthdate(currentBirthdate)
    setStreet(currentShippingAddress?.street || '')
    setCity(currentShippingAddress?.city || '')
    setState(currentShippingAddress?.state || '')
    setZip(currentShippingAddress?.zip || '')
    setCountry(currentShippingAddress?.country || 'USA')
    setShowOtpVerify(false)
    setOtpCode('')
    onClose()
  }

  const avatarColor = getAvatarColor(currentUsername)
  const initials = getInitials(
    displayName || currentDisplayName,
    currentUsername
  )

  const hasChanges =
    username.trim().toLowerCase() !== currentUsername ||
    displayName !== currentDisplayName ||
    bio !== currentBio ||
    phoneNumber !== currentPhoneNumber ||
    birthdate !== currentBirthdate ||
    street !== (currentShippingAddress?.street || '') ||
    city !== (currentShippingAddress?.city || '') ||
    state !== (currentShippingAddress?.state || '') ||
    zip !== (currentShippingAddress?.zip || '') ||
    country !== (currentShippingAddress?.country || 'USA')

  const hasPhoneOnProfile = !!currentPhoneNumber

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <SheetContent className="bg-neutral-950 border-white/[0.06] flex flex-col h-full max-h-[90vh] w-full sm:max-w-md">
        {/* Header */}
        <SheetHeader className="flex-shrink-0 flex flex-row items-center justify-between pb-6 border-b border-white/[0.06]">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-emerald-400" />
            <SheetTitle className="text-xl font-bold text-white">
              {t('profileSettings')}
            </SheetTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCancel}
            className="text-neutral-400 hover:text-white hover:bg-white/[0.06] h-8 w-8 transition-colors"
          >
            <X className="w-5 h-5" />
          </Button>
        </SheetHeader>

        {/* Scrollable Form */}
        <div className="flex-1 overflow-y-auto py-6 px-6 space-y-6">
          {/* Avatar Preview */}
          <div className="text-center space-y-3 pb-6 border-b border-white/[0.06]">
            <div
              className={`w-20 h-20 rounded-full bg-gradient-to-br ${avatarColor} mx-auto flex items-center justify-center shadow-lg shadow-emerald-500/10`}
            >
              <span className="text-black text-2xl font-bold">{initials}</span>
            </div>
            <div>
              <p className="text-lg font-semibold text-white">
                {displayName || currentUsername}
              </p>
              <p className="text-sm text-neutral-400 font-mono">
                @{currentUsername}
              </p>
            </div>
          </div>

          {/* Missing Phone Warning */}
          {!currentPhoneNumber && !showOtpVerify && (
            <div className="rounded-xl bg-amber-500/10 border border-amber-500/20 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-amber-400 font-semibold text-sm mb-1">
                    {t('phoneRequired')}
                  </p>
                  <p className="text-amber-400/80 text-xs leading-relaxed">
                    {t('phoneRequiredDesc')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Phone OTP Verification (shown after saving a new phone) */}
          {showOtpVerify && (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-5 space-y-4">
              <div className="flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-emerald-400 font-semibold text-sm">
                    {t('verifyPhone')}
                  </p>
                  <p className="text-emerald-400/70 text-xs mt-1">
                    {t('verifyPhoneDesc', { phone: pendingPhone })}
                  </p>
                </div>
              </div>
              <Input
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={otpCode}
                onChange={(e) =>
                  setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                className="bg-neutral-900/80 border-emerald-500/30 text-white text-center text-2xl tracking-[0.5em] font-mono focus:ring-2 focus:ring-emerald-500/30"
                maxLength={6}
                autoFocus
                disabled={isVerifying}
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleVerifyOtp}
                  disabled={otpCode.length < 6 || isVerifying}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-black font-semibold disabled:opacity-40"
                >
                  {isVerifying ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  {isVerifying ? t('verifying') : t('verifyCode')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowOtpVerify(false)
                    setOtpCode('')
                  }}
                  disabled={isVerifying}
                  className="text-neutral-400 hover:text-white"
                >
                  {t('skipForNow')}
                </Button>
              </div>
              <p className="text-xs text-neutral-500">
                {t('verifyPhoneSkipNote')}
              </p>
            </div>
          )}

          {/* Account Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-neutral-300 uppercase tracking-wide">
              {t('accountInformation')}
            </h3>

            {/* Email (Read-only) */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-neutral-400 text-xs">
                📧 {t('emailLabel')}
              </Label>
              <Input
                id="email"
                value={currentEmail}
                disabled
                className="bg-neutral-900/30 border-white/[0.04] text-neutral-500 cursor-not-allowed"
              />
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <Label
                htmlFor="phone"
                className="text-neutral-300 flex items-center gap-2"
              >
                <Phone className="w-4 h-4" />
                {t('phoneNumberLabel')}
                {!currentPhoneNumber && (
                  <span className="text-xs text-amber-400 font-normal">
                    {t('required')}
                  </span>
                )}
                {currentPhoneNumber && phoneVerified && (
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                )}
                {currentPhoneNumber && !phoneVerified && (
                  <ShieldAlert className="w-3.5 h-3.5 text-amber-400" />
                )}
              </Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                disabled={showOtpVerify}
                className={`bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50 ${
                  !currentPhoneNumber ? 'border-amber-500/30' : ''
                }`}
              />
              <p className="text-xs text-neutral-500">
                {t('phoneEditHelp')}
              </p>
            </div>
          </div>

          {/* SMS Notifications (shown when phone exists) */}
          {hasPhoneOnProfile && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-neutral-400" />
                <h3 className="text-sm font-semibold text-neutral-300 uppercase tracking-wide">
                  {t('smsNotifications')}
                </h3>
              </div>

              <button
                type="button"
                onClick={handleSmsToggle}
                disabled={smsLoading}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border transition-all ${
                  smsOptedIn
                    ? 'bg-emerald-500/10 border-emerald-500/30'
                    : 'bg-neutral-900/50 border-white/[0.06] hover:border-white/[0.12]'
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    smsOptedIn ? 'bg-emerald-500/20' : 'bg-neutral-800'
                  }`}
                >
                  <Bell
                    className={`w-5 h-5 ${smsOptedIn ? 'text-emerald-400' : 'text-neutral-400'}`}
                  />
                </div>
                <div className="flex-1 text-left">
                  <p
                    className={`text-sm font-medium ${smsOptedIn ? 'text-emerald-400' : 'text-neutral-300'}`}
                  >
                    {t('smsOptInLabel')}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {t('smsOptInDesc')}
                  </p>
                </div>
                {smsLoading ? (
                  <Loader2 className="w-5 h-5 text-neutral-400 animate-spin flex-shrink-0" />
                ) : (
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                      smsOptedIn
                        ? 'bg-emerald-500 border-emerald-500'
                        : 'border-neutral-600 bg-transparent'
                    }`}
                  >
                    {smsOptedIn && (
                      <Check className="w-3.5 h-3.5 text-black" />
                    )}
                  </div>
                )}
              </button>

              {smsOptedIn && (
                <p className="text-xs text-neutral-500 leading-relaxed px-1">
                  {t('smsConsentDisclosure')}
                </p>
              )}
            </div>
          )}

          {/* Profile Customization */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-neutral-300 uppercase tracking-wide">
              {t('profileCustomization')}
            </h3>

            {/* Username */}
            <div className="space-y-2">
              <Label htmlFor="username" className="text-neutral-300">
                {t('username')} <span className="text-red-400">*</span>
              </Label>
              <Input
                id="username"
                value={username}
                onChange={(e) =>
                  setUsername(
                    e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')
                  )
                }
                disabled={!canChangeUsername}
                placeholder="username"
                className={`font-mono ${
                  canChangeUsername
                    ? 'bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20'
                    : 'bg-neutral-900/30 border-white/[0.04] text-neutral-500 cursor-not-allowed'
                }`}
                maxLength={20}
              />
              {!canChangeUsername && (
                <p className="text-xs text-amber-400">
                  ⏳{' '}
                  {t('usernameChangeCooldown', {
                    hours: hoursUntilUsernameChange,
                  })}
                </p>
              )}
              {canChangeUsername && (
                <p className="text-xs text-neutral-500">
                  {t('usernameChangeHelp')}
                </p>
              )}
            </div>

            {/* Display Name */}
            <div className="space-y-2">
              <Label htmlFor="displayName" className="text-neutral-300">
                {t('displayName')}{' '}
                <span className="text-neutral-500 text-xs font-normal">
                  {t('optional')}
                </span>
              </Label>
              <Input
                id="displayName"
                placeholder={t('yourName')}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
                maxLength={50}
              />
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label htmlFor="bio" className="text-neutral-300">
                {t('bio')}{' '}
                <span className="text-neutral-500 text-xs font-normal">
                  {t('optional')}
                </span>
              </Label>
              <Textarea
                id="bio"
                placeholder="Professional trader with 10+ years of experience."
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 min-h-[80px] focus:border-emerald-500/50 focus:ring-emerald-500/20"
                maxLength={160}
              />
              <p
                className={`text-xs text-right font-mono ${
                  bio.length > 150
                    ? 'text-amber-400'
                    : bio.length > 140
                      ? 'text-amber-500'
                      : 'text-neutral-500'
                }`}
              >
                {bio.length}/160
              </p>
            </div>
          </div>

          {/* Personal Information */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-neutral-400" />
              <h3 className="text-sm font-semibold text-neutral-300 uppercase tracking-wide">
                {t('personalInformation')}
              </h3>
              <span className="text-xs text-neutral-500">{t('optional')}</span>
            </div>

            <div className="space-y-2">
              <Label htmlFor="birthdate" className="text-neutral-300">
                {t('birthdate')}
              </Label>
              <Input
                id="birthdate"
                type="date"
                value={birthdate}
                onChange={(e) => setBirthdate(e.target.value)}
                className="bg-neutral-900/80 border-white/[0.08] text-white focus:border-emerald-500/50 focus:ring-emerald-500/20 [color-scheme:dark]"
              />
              <p className="text-xs text-neutral-500">{t('birthdateHelp')}</p>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-neutral-400" />
              <h3 className="text-sm font-semibold text-neutral-300 uppercase tracking-wide">
                {t('shippingAddress')}
              </h3>
              <span className="text-xs text-neutral-500">{t('optional')}</span>
            </div>
            <p className="text-xs text-neutral-500 -mt-2">
              {t('shippingAddressHelp')}
            </p>

            <div className="space-y-2">
              <Label htmlFor="street" className="text-neutral-300">
                {t('streetAddress')}
              </Label>
              <Input
                id="street"
                placeholder="123 Main St, Apt 4B"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="city" className="text-neutral-300">
                  {t('city')}
                </Label>
                <Input
                  id="city"
                  placeholder="Los Angeles"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state" className="text-neutral-300">
                  {t('state')}
                </Label>
                <Input
                  id="state"
                  placeholder="CA"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
                  maxLength={20}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="zip" className="text-neutral-300">
                  {t('zipCode')}
                </Label>
                <Input
                  id="zip"
                  placeholder="90001"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
                  maxLength={10}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country" className="text-neutral-300">
                  {t('country')}
                </Label>
                <Input
                  id="country"
                  placeholder="USA"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="bg-neutral-900/80 border-white/[0.08] text-white placeholder:text-neutral-500 focus:border-emerald-500/50 focus:ring-emerald-500/20"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex-shrink-0 grid grid-cols-2 gap-3 pt-4 px-6 border-t border-white/[0.06]">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="bg-neutral-900/50 border-white/[0.08] text-white hover:bg-neutral-800 hover:text-white font-medium"
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              !hasChanges ||
              updateProfileMutation.isPending ||
              showOtpVerify
            }
            className="bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 text-black font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateProfileMutation.isPending ? t('saving') : t('saveChanges')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}
